import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Star, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useCreateAchievement, useSuggestions } from '@/hooks/useAchievements'
import { RATING_COLORS } from '@/lib/rating-colors'

const schema = z.object({
  title: z.string().min(1, 'タイトルは必須です').max(255),
  score: z.string().optional(),
  rating: z.number().min(1).max(5),
  achieved_at: z.string().min(1),
  memo: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function toLocalDateString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

type Props = {
  open: boolean
  onClose: () => void
}

export default function AchievementCreateSheet({ open, onClose }: Props) {
  const createAchievement = useCreateAchievement()
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [rating, setRating] = useState(1)
  const [titleQuery, setTitleQuery] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  const { data: suggestions = [] } = useSuggestions(titleQuery)

  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      rating: 1,
      achieved_at: toLocalDateString(new Date()),
    },
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleClose = () => {
    reset({ title: '', score: '', rating: 1, achieved_at: toLocalDateString(new Date()), memo: '' })
    setTags([])
    setTagInput('')
    setRating(1)
    setTitleQuery('')
    setShowSuggestions(false)
    onClose()
  }

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setTagInput('')
  }

  const onSubmit = (values: FormValues) => {
    createAchievement.mutate(
      {
        title: values.title,
        score: values.score ?? '',
        rating: values.rating,
        tags,
        memo: values.memo ?? '',
        achieved_at: `${values.achieved_at}T00:00:00.000Z`,
      },
      {
        onSuccess: () => {
          toast.success('実績を登録しました')
          handleClose()
        },
        onError: () => toast.error('登録に失敗しました'),
      },
    )
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleClose() }}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>実績を登録</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* タイトル（サジェスト付き） */}
          <div className="space-y-1.5 relative" ref={suggestionsRef}>
            <label className="text-sm font-medium">タイトル *</label>
            <Input
              {...register('title')}
              placeholder="実績名を入力"
              onChange={(e) => {
                register('title').onChange(e)
                setTitleQuery(e.target.value)
                setShowSuggestions(true)
              }}
              onFocus={() => setShowSuggestions(true)}
              autoComplete="off"
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-popover border rounded-lg shadow-md overflow-hidden">
                {suggestions.slice(0, 6).map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted flex items-center justify-between"
                    onClick={() => { setValue('title', s.title); setTitleQuery(''); setShowSuggestions(false) }}
                  >
                    <span>{s.title}</span>
                    <span className="text-xs text-muted-foreground ml-2 shrink-0">{s.source}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* スコア・結果 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">スコア・結果（任意）</label>
            <Input {...register('score')} placeholder="例: 850点、1位、○○賞" />
          </div>

          {/* 評価 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">評価</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => { setRating(n); setValue('rating', n) }}
                  className="p-1"
                >
                  <Star
                    size={24}
                    className={n <= rating ? RATING_COLORS.self : RATING_COLORS.empty}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* 日付 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">日付</label>
            <Input {...register('achieved_at')} type="date" />
          </div>

          {/* タグ */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">タグ（任意）</label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                placeholder="タグを入力して Enter"
              />
              <Button type="button" variant="outline" onClick={addTag} size="sm">追加</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 text-xs bg-secondary text-secondary-foreground rounded-full px-2.5 py-1">
                    {tag}
                    <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* メモ */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">メモ（任意）</label>
            <Textarea {...register('memo')} placeholder="振り返りを自由に記入..." className="min-h-0 h-9 resize-none" />
          </div>

          <Button type="submit" className="w-full" disabled={createAchievement.isPending}>
            {createAchievement.isPending ? '登録中...' : '登録する'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}
